<script setup lang="ts">
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const { t, tm, rt } = useI18n()
const localePath = useLocalePath()

const sectionKeys = ['introduction', 'data_collect', 'how_use', 'sharing', 'rights', 'contact'] as const
type SectionKey = typeof sectionKeys[number]

const sectionIds: Record<SectionKey, string> = {
  introduction: 'pp-introduction',
  data_collect: 'pp-data-collect',
  how_use: 'pp-how-use',
  sharing: 'pp-sharing',
  rights: 'pp-rights',
  contact: 'pp-contact',
}

const activeId = ref(sectionIds.introduction)

const dataItems = computed(() => {
  const raw = tm('page.privacy_policy.doc.sections.data_collect.items') as Array<{ label: unknown; desc: unknown }>
  return raw.map(i => ({ label: rt(i.label), desc: rt(i.desc) }))
})

const sidebarRef = ref<HTMLElement | null>(null)
const mainRef = ref<HTMLElement | null>(null)

let observer: IntersectionObserver | null = null
let mm: ReturnType<typeof gsap.matchMedia> | null = null

onMounted(() => {
  // Active section tracking
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) activeId.value = entry.target.id
      }
    },
    { rootMargin: '-15% 0px -75% 0px', threshold: 0 },
  )
  sectionKeys.forEach(k => {
    const el = document.getElementById(sectionIds[k])
    if (el) observer!.observe(el)
  })

  // GSAP sticky sidebar — Y-only transform, RTL-safe.
  // pin:true sets position:fixed which breaks in RTL by jumping horizontally.
  // Instead, scrub a translateY equal to the scroll distance so the sidebar
  // appears fixed. Math: viewport_y = natural_y - scroll + translateY.
  // With translateY == scroll (scrub 1:1), viewport_y stays constant = sticky.
  nextTick(() => {
    const sidebar = sidebarRef.value
    const main = mainRef.value
    if (!sidebar || !main) return

    const distance = () => main.offsetHeight - sidebar.offsetHeight

    mm = gsap.matchMedia()
    mm.add('(min-width: 1024px)', () => {
      gsap.fromTo(
        sidebar,
        { y: 0 },
        {
          y: distance,
          ease: 'none',
          scrollTrigger: {
            trigger: sidebar,
            start: 'top 80px',
            end: () => `+=${distance()}`,
            scrub: true,
            invalidateOnRefresh: true,
          },
        },
      )
    })
  })
})

onUnmounted(() => {
  observer?.disconnect()
  mm?.kill()
})

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<template>
  <section class="bg-white py-14 md:py-20">
    <div class="container">
      <div class="grid grid-cols-1 lg:grid-cols-[272px_1fr] gap-10 lg:gap-16 items-start">

        <!-- ── Sidebar TOC ── -->
        <aside ref="sidebarRef">
          <div class="border border-gray-200 rounded-xl p-5">

            <p class="text-sm font-bold text-gray-900 mb-4">
              {{ t('page.privacy_policy.doc.toc_title') }}
            </p>

            <nav class="flex flex-col gap-0.5">
              <button
                v-for="(key, index) in sectionKeys"
                :key="key"
                class="w-full text-start text-sm px-3 py-2 rounded-lg font-medium transition-all duration-150 cursor-pointer"
                :class="activeId === sectionIds[key]
                  ? 'bg-sky-blue/10 text-sky-blue'
                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'"
                @click="scrollTo(sectionIds[key])">
                {{ index + 1 }}. {{ t(`page.privacy_policy.doc.sections.${key}.nav`) }}
              </button>
            </nav>

            <!-- Need Help -->
            <div class="mt-7 pt-5 border-t border-gray-100">
              <p class="text-sm font-bold text-gray-900 mb-1">
                {{ t('page.privacy_policy.doc.need_help') }}
              </p>
              <p class="text-sm text-gray-400 mb-4">
                {{ t('page.privacy_policy.doc.need_help_desc') }}
              </p>
              <NuxtLink
                :to="localePath('/contact-us')"
                class="bfm-btn bfm-btn-sm bg-cheese-yellow w-full justify-center text-sm">
                {{ t('page.privacy_policy.doc.contact_team_btn') }}
              </NuxtLink>
            </div>
          </div>
        </aside>

        <!-- ── Main Content ── -->
        <main ref="mainRef" class="flex flex-col divide-y divide-gray-100">

          <!-- 1 · Introduction -->
          <div :id="sectionIds.introduction" class="pb-12">
            <div class="flex items-center gap-3 mb-5">
              <span class="size-7 rounded-md bg-heart-purple text-white text-xs font-bold flex items-center justify-center shrink-0 border-2 border-gray-900 shadow-[3px_3px_0_#111827]">1</span>
              <h2 class="text-2xl md:text-3xl font-bold text-deep-indigo">
                {{ t('page.privacy_policy.doc.sections.introduction.title') }}
              </h2>
            </div>
            <p class="text-gray-600 leading-relaxed mb-4">
              {{ t('page.privacy_policy.doc.sections.introduction.p1') }}
            </p>
            <p class="text-gray-600 leading-relaxed">
              {{ t('page.privacy_policy.doc.sections.introduction.p2') }}
            </p>
          </div>

          <!-- 2 · Data We Collect -->
          <div :id="sectionIds.data_collect" class="py-12">
            <div class="flex items-center gap-3 mb-5">
              <span class="size-7 rounded-md bg-heart-purple text-white text-xs font-bold flex items-center justify-center shrink-0 border-2 border-gray-900 shadow-[3px_3px_0_#111827]">2</span>
              <h2 class="text-2xl md:text-3xl font-bold text-deep-indigo">
                {{ t('page.privacy_policy.doc.sections.data_collect.title') }}
              </h2>
            </div>
            <p class="text-gray-600 leading-relaxed mb-6">
              {{ t('page.privacy_policy.doc.sections.data_collect.intro') }}
            </p>
            <div class="flex flex-col gap-3">
              <div
                v-for="item in dataItems"
                :key="item.label"
                class="bg-gray-50 rounded-lg px-5 py-3.5 text-sm text-gray-700">
                <span class="font-bold text-gray-900">{{ item.label }}:</span> {{ item.desc }}
              </div>
            </div>
          </div>

          <!-- 3 · How We Use Data -->
          <div :id="sectionIds.how_use" class="py-12">
            <div class="flex items-center gap-3 mb-5">
              <span class="size-7 rounded-md bg-heart-purple text-white text-xs font-bold flex items-center justify-center shrink-0 border-2 border-gray-900 shadow-[3px_3px_0_#111827]">3</span>
              <h2 class="text-2xl md:text-3xl font-bold text-deep-indigo">
                {{ t('page.privacy_policy.doc.sections.how_use.title') }}
              </h2>
            </div>
            <p class="text-gray-600 leading-relaxed mb-4">
              {{ t('page.privacy_policy.doc.sections.how_use.p1') }}
            </p>
            <p class="text-gray-600 leading-relaxed mb-6">
              {{ t('page.privacy_policy.doc.sections.how_use.p2') }}
            </p>
            <!-- Security note callout -->
            <div class="border-s-4 border-sky-blue bg-sky-blue/5 rounded-e-lg px-5 py-4">
              <p class="text-sm font-bold text-sky-blue mb-1">
                {{ t('page.privacy_policy.doc.sections.how_use.note_title') }}
              </p>
              <p class="text-sm text-sky-blue/80 leading-relaxed">
                {{ t('page.privacy_policy.doc.sections.how_use.note') }}
              </p>
            </div>
          </div>

          <!-- 4 · Sharing & Partners -->
          <div :id="sectionIds.sharing" class="py-12">
            <div class="flex items-center gap-3 mb-5">
              <span class="size-7 rounded-md bg-heart-purple text-white text-xs font-bold flex items-center justify-center shrink-0 border-2 border-gray-900 shadow-[3px_3px_0_#111827]">4</span>
              <h2 class="text-2xl md:text-3xl font-bold text-deep-indigo">
                {{ t('page.privacy_policy.doc.sections.sharing.title') }}
              </h2>
            </div>
            <p class="text-gray-600 leading-relaxed">
              {{ t('page.privacy_policy.doc.sections.sharing.p1') }}
            </p>
          </div>

          <!-- 5 · Your Rights -->
          <div :id="sectionIds.rights" class="py-12">
            <div class="flex items-center gap-3 mb-5">
              <span class="size-7 rounded-md bg-heart-purple text-white text-xs font-bold flex items-center justify-center shrink-0 border-2 border-gray-900 shadow-[3px_3px_0_#111827]">5</span>
              <h2 class="text-2xl md:text-3xl font-bold text-deep-indigo">
                {{ t('page.privacy_policy.doc.sections.rights.title') }}
              </h2>
            </div>
            <p class="text-gray-600 leading-relaxed">
              {{ t('page.privacy_policy.doc.sections.rights.p1') }}
            </p>
          </div>

          <!-- 6 · Contact Us -->
          <div :id="sectionIds.contact" class="pt-12">
            <div class="flex items-center gap-3 mb-5">
              <span class="size-7 rounded-md bg-heart-purple text-white text-xs font-bold flex items-center justify-center shrink-0 border-2 border-gray-900 shadow-[3px_3px_0_#111827]">6</span>
              <h2 class="text-2xl md:text-3xl font-bold text-deep-indigo">
                {{ t('page.privacy_policy.doc.sections.contact.title') }}
              </h2>
            </div>
            <p class="text-gray-600 leading-relaxed mb-4">
              {{ t('page.privacy_policy.doc.sections.contact.p1') }}
            </p>
            <a
              href="mailto:hello@bookforme.com"
              class="text-base font-bold text-gray-900 hover:text-sky-blue transition-colors">
              {{ t('page.privacy_policy.doc.sections.contact.email') }}
            </a>
          </div>

        </main>
      </div>
    </div>
  </section>
</template>
